import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

const CropVideo = () => {
  const { name, place, date, hour, videoName } = useParams();
  const navigate = useNavigate();

  // Estados dos Controles
  const [zoom, setZoom] = useState(1.0);
  const [format, setFormat] = useState('9:16');
  
  // Estados de Processamento
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Histórico de Crops (Virá do DynamoDB)
  const [existingCrops, setExistingCrops] = useState([]);

  // URL base do seu bucket oficial
  const S3_BASE_URL = "https://replaypro.com.br";
  const originalVideoUrl = `${S3_BASE_URL}${videoName}`;

  useEffect(() => {
    // Aqui você fará um GET para a sua API consultar o DynamoDB
    // Exemplo: fetch(`/api/crops/history?original=${videoName}`)
    // Simulando a busca no DynamoDB:
    fetchExistingCrops();
  }, [videoName]);

  const fetchExistingCrops = async () => {
    // TODO: Conectar com sua rota real do API Gateway / DynamoDB
    // Por enquanto, deixamos vazio ou mockado para a UI
    setExistingCrops([
      /* Exemplo de retorno do banco:
      { format: "9:16", zoom: 1.5, url: `${S3_BASE_URL}/crops/${videoName.replace('.mp4', '_zoom1.5_9x16.mp4')}` }
      */
    ]);
  };

  const handleGenerateCrop = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Endpoint da sua HTTP API na Virginia
      const API_URL = "https://1jfp6fzlxf.execute-api.us-east-1.amazonaws.com/items/crop"; 

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filePath: `${videoName}`,
          zoom: parseFloat(zoom),
          format: format
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Processamento iniciado! O vídeo será gerado pela IA da Replay Pro em instantes.' 
        });
        
        // Opcional: Adicionar na tabela do DynamoDB via API que este crop está "EM PROCESSAMENTO"
      } else {
        throw new Error(data.error || 'Erro ao processar vídeo.');
      }
    } catch (error) {
      setMessage({ type: 'danger', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4">
      {/* BREADCRUMB / NAVEGAÇÃO */}
      <div className="mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i> Voltar para os Replays
        </Button>
        <h4 className="mt-3">
          Estúdio de Recorte Inteligente (IA)
        </h4>
        <p className="text-muted">
          {decodeURIComponent(name)} - {decodeURIComponent(place)} | {date} - {hour}:00
        </p>
      </div>

      <Row>
        {/* COLUNA ESQUERDA: VÍDEO ORIGINAL */}
        <Col lg={7} className="mb-4">
          <Card className="shadow-sm">
            <Card.Header className="bg-dark text-white font-weight-bold">
              Vídeo Original
            </Card.Header>
            <Card.Body className="p-0 text-center bg-black">
              <video width="100%" controls style={{ maxHeight: '500px' }}>
                <source src={originalVideoUrl} type="video/mp4" />
                Seu navegador não suporta vídeos.
              </video>
            </Card.Body>
          </Card>
        </Col>

        {/* COLUNA DIREITA: CONTROLES E HISTÓRICO */}
        <Col lg={5}>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h5 className="mb-4">Configurações de Recorte</h5>
              
              <Form>
                {/* SELECT FORMATO */}
                <Form.Group className="mb-4">
                  <Form.Label className="font-weight-bold">Formato de Tela</Form.Label>
                  <Form.Select 
                    value={format} 
                    onChange={(e) => setFormat(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="9:16">📱 Reels / TikTok (9:16)</option>
                    <option value="4:5">📱 Feed Retrato (4:5)</option>
                    <option value="11:14">📸 Instagram Otimizado (11:14)</option>
                    <option value="1:1">⬛ Feed Quadrado (1:1)</option>
                    <option value="16:9">💻 YouTube Expandido (16:9)</option>
                  </Form.Select>
                </Form.Group>

                {/* SLIDER DE ZOOM */}
                <Form.Group className="mb-4">
                  <Form.Label className="font-weight-bold d-flex justify-content-between">
                    <span>Nível de Zoom</span>
                    <span className="text-danger">{zoom}x</span>
                  </Form.Label>
                  <Form.Range 
                    min={1.0} 
                    max={2.0} 
                    step={0.1} 
                    value={zoom} 
                    onChange={(e) => setZoom(e.target.value)}
                    disabled={isLoading}
                  />
                  <Form.Text className="text-muted">
                    Ajuste o zoom para aproximar a câmera da bola.
                  </Form.Text>
                </Form.Group>

                {/* BOTÃO GERAR */}
                <div className="d-grid gap-2">
                  <Button 
                    variant="danger" 
                    size="lg" 
                    onClick={handleGenerateCrop}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Processando IA...</>
                    ) : (
                      <>✨ Gerar Novo Recorte</>
                    )}
                  </Button>
                </div>
              </Form>

              {/* MENSAGENS DE FEEDBACK */}
              {message.text && (
                <Alert variant={message.type} className="mt-3 mb-0">
                  {message.text}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* SESSÃO DE CROPS JÁ GERADOS (CACHE / DYNAMODB) */}
          <Card className="shadow-sm border-0 bg-light">
            <Card.Body>
              <h6 className="text-muted mb-3">
                <i className="bi bi-clock-history"></i> Recortes Disponíveis
              </h6>
              
              {existingCrops.length === 0 ? (
                <p className="text-muted small mb-0">Nenhum recorte inteligente foi gerado para este vídeo ainda.</p>
              ) : (
                <Row>
                  {existingCrops.map((crop, index) => (
                    <Col xs={6} key={index} className="mb-2">
                      <Button 
                        variant="outline-dark" 
                        className="w-100 text-left d-flex flex-column"
                        onClick={() => window.open(crop.url, "_blank")}
                      >
                        <span className="font-weight-bold">{crop.format}</span>
                        <span className="small text-muted">Zoom: {crop.zoom}x</span>
                      </Button>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>

        </Col>
      </Row>
    </div>
  );
};

export default CropVideo;